import React, { useCallback, useEffect, useRef, useState } from 'react'
import pa from '../ProductActivityPage.module.css'
import styles from './Pallets.module.css'
import os from './Orders.module.css'
import ls from './Loads.module.css'
import o from '../../components/Overlay.module.css'
import sd from './SdPanel.module.css'
import { CloseButton, SdPanel } from './SdPanel'
import { apiJson } from '../../lib/api'
import { useHelpPref } from '../../lib/preferences'
import { useLayer, useChanged } from '../../components/layers'
import { PendingButton, inertClass, useSlow } from '../../components/pending'
import { hasCapability, useAccess } from '../../lib/access'
import { PalletDrawer } from './PalletDrawer'
import { OrderModal } from './OrderModal'
import { PSTATE, fmtStamp, srcShort } from './model'
import { approvalChip, type Chip } from './orders'
import {
  FREIGHT_NOTE,
  SCAN_TONE,
  candidateText,
  departedNote,
  formFrom,
  formValid,
  loadChip,
  loadFacts,
  progress,
  verificationChip,
  withCurrentRef,
  type DetailsForm,
  type GatedPallet,
  type Load,
  type LoadOptions,
  type LoadRecord,
} from './loads'
import { openableRow } from '../../lib/openableRow'
import { DateField } from '../../components/DateField'

// The load record (V4 load detail; handoff §7n.6): the truck, the pallets
// and orders on it, loading verification, the paperwork each order needs,
// and its history. Every move is a POST the API validates again; the record
// is replaced by the one each move returns, and `moves` decides what shows.

export type LoadTab = 'load' | 'op' | 'docs' | 'hist'

const TABS: [LoadTab, string][] = [
  ['load', 'Load details'],
  ['op', 'Orders & pallets'],
  ['docs', 'Documents'],
  ['hist', 'History'],
]

const errText = (e: any) => e?.body?.error || e?.message || String(e)
const errList = (e: any): string[] => (Array.isArray(e?.body?.errors) && e.body.errors.length ? e.body.errors : [errText(e)])

const ChipEl = ({ c }: { c: Chip }) => (
  <span className={styles.chip} style={{ background: c.bg, color: c.ink }}>
    {c.label}
  </span>
)

type Result = { message: string; record: LoadRecord; outcome?: string; tone?: string }

export function LoadModal({
  user,
  loadId,
  initialTab = 'load',
  onClose,
  onChanged,
}: {
  user: any
  loadId: string
  initialTab?: LoadTab
  onClose: () => void
  // a message is a toast; none means only "refetch the list"
  onChanged: (message?: string) => void
}) {
  const [rec, setRec] = useState<LoadRecord | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<LoadTab>(initialTab)
  const [panel, setPanel] = useState<null | 'edit' | 'cancel'>(null)
  // The move in flight, by its path ('ready', 'depart', 'pallets/<id>/remove',
  // 'scan'), so only the pressed button says what it is doing (h 3975-3983).
  const [busyOn, setBusyOn] = useState<string | null>(null)
  const busy = busyOn !== null
  const slow = useSlow(busy)
  const [scan, setScan] = useState('')
  const [scanMsg, setScanMsg] = useState<{ tone: string; text: string } | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [palletId, setPalletId] = useState<string | null>(null)
  const [orderId, setOrderId] = useState<string | null>(null)
  const [helpOn, toggleHelp] = useHelpPref(user, 'load_record')
  const mayManage = hasCapability(useAccess(), 'manage_stock_dispatch')

  const userRef = useRef(user)
  userRef.current = user
  const load = useCallback(async () => {
    try {
      setRec(await apiJson<LoadRecord>(userRef.current, `/api/dispatches/${loadId}`))
      setError(null)
    } catch (e) {
      setError(`Failed to load the load: ${errText(e)}`)
    }
  }, [loadId])

  useEffect(() => {
    load()
  }, [load])

  // Escape closes the topmost layer only (h 425-458). The record is a layer;
  // the cancel confirm, the edit form, Add pallets, a pallet drawer and an
  // order opened from this load each register their own, so the stack order
  // peels them one press at a time. h 3984-3992: busy prevents close.
  const modalEl = useRef<HTMLDivElement>(null)
  const modal = useLayer(true, onClose, { el: modalEl, modal: true, busy })
  const cancelPanel = useLayer(panel === 'cancel' && !!rec, () => setPanel(null), { busy })
  // The record's ways out of the edit form that are not its own close (the
  // Edit details toggle, Cancel load, Start loading, another tab) ask first
  // when it holds changes: the record's guard asks for a layer over it. The
  // scrim and the × do the same through the record's own requestClose.
  const leaveEdit = modal.guard

  const post = async (path: string, body: Record<string, unknown> = {}): Promise<Result | null> => {
    setBusyOn(path)
    try {
      const r = await apiJson<Result>(userRef.current, `/api/dispatches/${loadId}/${path}`, { method: 'POST', body })
      setRec(r.record)
      setError(null)
      return r
    } catch (e) {
      setError(errText(e))
      return null
    } finally {
      setBusyOn(null)
    }
  }

  const move = async (path: string) => {
    const r = await post(path)
    if (!r) return
    setPanel(null)
    if (path === 'reset-loading' || path === 'verify-all') setScanMsg(path === 'verify-all' ? { tone: 'good', text: r.message } : null)
    onChanged(r.message)
  }

  const doScan = async () => {
    const code = scan.trim()
    if (!code || busy) return
    setBusyOn('scan')
    try {
      const r = await apiJson<Result>(userRef.current, `/api/dispatches/${loadId}/scan`, { method: 'POST', body: { code } })
      setRec(r.record)
      setScanMsg({ tone: r.tone || 'good', text: r.message })
      setScan('')
      onChanged()
    } catch (e) {
      setScanMsg({ tone: 'bad', text: errText(e) })
    } finally {
      setBusyOn(null)
    }
  }

  const l = rec?.load
  // Every move is a POST the API refuses without Manage stock & dispatch.
  const m = mayManage ? rec?.moves : undefined

  return (
    <>
      <div className={os.backdrop} onClick={modal.requestClose}>
        <div ref={modalEl} className={os.record} role="dialog" aria-label={l ? `Load ${l.reference}` : 'Load'} onClick={(e) => e.stopPropagation()}>
          <div className={os.recHead}>
            <div className={os.recTitleRow}>
              <span className={os.recTitle}>{l?.reference || 'Load'}</span>
              {l && <ChipEl c={loadChip(l.state)} />}
              <div className={styles.dHeadRight}>
                {m?.edit && (
                  <button
                    type="button"
                    className={pa.ghostBtn}
                    onClick={() =>
                      leaveEdit(() => {
                        setTab('load')
                        setPanel(panel === 'edit' ? null : 'edit')
                      })
                    }
                  >
                    Edit details
                  </button>
                )}
                {m?.add && (
                  <button type="button" className={pa.ghostBtn} onClick={() => setAddOpen(true)}>
                    Add pallets
                  </button>
                )}
                {m?.ready.on && !m.ready.blocked && (
                  <PendingButton className={pa.primaryBtn} busy={busyOn === 'ready'} pending="Marking ready…" disabled={busy} onClick={() => move('ready')}>
                    Mark ready to load
                  </PendingButton>
                )}
                {m?.start && (
                  <PendingButton
                    className={pa.primaryBtn}
                    busy={busyOn === 'start-loading'}
                    pending="Starting…"
                    disabled={busy}
                    onClick={() =>
                      leaveEdit(() => {
                        setTab('op')
                        move('start-loading')
                      })
                    }
                  >
                    Start loading
                  </PendingButton>
                )}
                {m?.cancel && (
                  <button type="button" className={styles.dangerBtn} disabled={busy} onClick={() => leaveEdit(() => setPanel(panel === 'cancel' ? null : 'cancel'))}>
                    Cancel load
                  </button>
                )}
                <CloseButton title="Close: the dispatch list stays behind" disabled={busy} onClick={modal.requestClose} />
              </div>
            </div>
            {l && (
              <div className={os.recSub}>
                {(l.carrier?.name || 'No carrier') + ' · ' + (l.pallets + ' pallet' + (l.pallets === 1 ? '' : 's')) + ' · ' + l.boxes + ' boxes'}
                {l.location ? ' · ' + l.location.name : ''}
              </div>
            )}
            {rec?.moves.ready.on && rec.moves.ready.blocked && <div className={ls.blocked}>{rec.moves.ready.blocked}</div>}
            {l?.departed && <div className={ls.departed}>{departedNote(l)}</div>}
            {error && (
              <div className={styles.alert} role="alert">
                {error}
              </div>
            )}
            {panel === 'cancel' && l && (
              <div className={os.inlinePanel} data-testid="cancel-panel">
                <div className={styles.strong}>Cancel {l.reference}?</div>
                <div className={styles.sub}>
                  Its pallets come off the load and stay in stock with their order quantities unchanged. The load keeps a record of what was on it.
                </div>
                <div className={styles.actions}>
                  <PendingButton className={styles.dangerFill} busy={busyOn === 'cancel'} pending="Cancelling…" disabled={busy} onClick={() => move('cancel')}>
                    Cancel load
                  </PendingButton>
                  <button type="button" className={pa.ghostBtn} disabled={busy} onClick={cancelPanel.requestClose}>
                    Keep it
                  </button>
                </div>
                {cancelPanel.confirm}
              </div>
            )}
            <div className={os.recTabs}>
              <div className={pa.tabTrack} role="tablist" aria-label="Load record">
                {TABS.map(([k, t]) => (
                  <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? pa.tabOn : pa.tab} onClick={() => (k === tab ? undefined : leaveEdit(() => setTab(k)))}>
                    {t}
                  </button>
                ))}
              </div>
              <button type="button" className={pa.helpBtn} style={{ marginLeft: 'auto' }} onClick={toggleHelp}>
                {helpOn ? 'Hide help' : 'How this tab works'}
              </button>
            </div>
          </div>

          <div className={`${styles.dBody} ${inertClass(busy, slow)}`}>
            {helpOn && <TabHelp tab={tab} onClose={toggleHelp} />}
            {rec && tab === 'load' && panel === 'edit' && (
              <EditDetails
                user={user}
                load={rec.load}
                onCancel={() => setPanel(null)}
                onSaved={(r) => {
                  setRec(r.record)
                  setPanel(null)
                  onChanged(r.message)
                }}
              />
            )}
            {rec && tab === 'load' && panel !== 'edit' && (
              <>
                <div className={ls.facts}>
                  {loadFacts(rec.load).map((f) => (
                    <div key={f.k} className={ls.fact} data-testid="load-fact">
                      <span>{f.k}</span>
                      <span>{f.v}</span>
                    </div>
                  ))}
                </div>
                <div className={styles.note} style={{ flex: 'none' }}>
                  {FREIGHT_NOTE}
                </div>
              </>
            )}
            {rec && tab === 'op' && (
              <OrdersAndPallets
                rec={rec}
                mayManage={mayManage}
                busyOn={busyOn}
                scan={scan}
                setScan={setScan}
                scanMsg={scanMsg}
                onScan={doScan}
                onMove={move}
                onRemove={async (id) => {
                  const r = await post(`pallets/${id}/remove`)
                  if (r) onChanged(r.message)
                }}
                onClearUnexpected={async (id) => {
                  const r = await post(`verifications/${id}/clear`)
                  if (r) onChanged(r.message)
                }}
                onOpenPallet={setPalletId}
                onOpenOrder={setOrderId}
              />
            )}
            {rec && tab === 'docs' && <DocumentsTab rec={rec} />}
            {rec && tab === 'hist' && <HistoryTab rec={rec} />}
          </div>
        </div>
        {modal.confirm}
      </div>

      {addOpen && l && (
        <AddPalletsPanel
          user={user}
          load={l}
          onClose={() => setAddOpen(false)}
          onAdded={(r) => {
            setAddOpen(false)
            setRec(r.record)
            setTab('op')
            onChanged(r.message)
          }}
        />
      )}
      {palletId && (
        <PalletDrawer
          user={user}
          palletId={palletId}
          onClose={() => setPalletId(null)}
          onChanged={(msg) => {
            load()
            onChanged(msg)
          }}
        />
      )}
      {orderId && (
        <OrderModal
          user={user}
          orderId={orderId}
          onClose={() => setOrderId(null)}
          onChanged={(msg) => {
            load()
            onChanged(msg)
          }}
          onNotice={(msg) => onChanged(msg)}
        />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Load details: shared by New load and Edit details
// ---------------------------------------------------------------------------

export function DetailsFields({
  form,
  set,
  options,
  current,
}: {
  form: DetailsForm
  set: (patch: Partial<DetailsForm>) => void
  options: LoadOptions
  current?: Load
}) {
  const carriers = withCurrentRef(options.carriers, current?.carrier || null)
  const locations = withCurrentRef(options.locations, current?.location || null)
  return (
    <div className={ls.form}>
      <label className={ls.field}>
        <span className={styles.fieldLabel}>Departure date</span>
        <DateField className={styles.input} value={form.planned_date} onChange={(e) => set({ planned_date: e.target.value })} />
      </label>
      <label className={ls.field}>
        <span className={styles.fieldLabel}>Departure time</span>
        <input type="time" className={styles.input} value={form.planned_time} onChange={(e) => set({ planned_time: e.target.value })} />
      </label>
      <label className={ls.field}>
        <span className={styles.fieldLabel}>Carrier</span>
        <select className={styles.input} value={form.carrier_id} onChange={(e) => set({ carrier_id: e.target.value })}>
          <option value="">Choose a carrier</option>
          {carriers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
      <label className={ls.field}>
        <span className={styles.fieldLabel}>Collection location</span>
        <select className={styles.input} value={form.location_id} onChange={(e) => set({ location_id: e.target.value })}>
          <option value="">Not recorded</option>
          {locations.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
      <label className={ls.field}>
        <span className={styles.fieldLabel}>Vehicle</span>
        <input className={styles.input} value={form.vehicle} onChange={(e) => set({ vehicle: e.target.value })} />
      </label>
      <label className={ls.field}>
        <span className={styles.fieldLabel}>Booking reference</span>
        <input className={styles.input} value={form.booking} onChange={(e) => set({ booking: e.target.value })} />
      </label>
      <label className={ls.field}>
        <span className={styles.fieldLabel}>Driver</span>
        <input className={styles.input} value={form.driver_name} onChange={(e) => set({ driver_name: e.target.value })} />
      </label>
      <label className={ls.field}>
        <span className={styles.fieldLabel}>Driver contact</span>
        <input className={styles.input} value={form.driver_contact} onChange={(e) => set({ driver_contact: e.target.value })} />
      </label>
      <label className={`${ls.field} ${styles.full}`}>
        <span className={styles.fieldLabel}>Instructions</span>
        <textarea className={os.textarea} value={form.instructions} onChange={(e) => set({ instructions: e.target.value })} />
      </label>
    </div>
  )
}

function EditDetails({
  user,
  load,
  onCancel,
  onSaved,
}: {
  user: any
  load: Load
  onCancel: () => void
  onSaved: (r: Result) => void
}) {
  const [form, setForm] = useState<DetailsForm>(() => formFrom(load))
  const [options, setOptions] = useState<LoadOptions | null>(null)
  const [errors, setErrors] = useState<string[]>([])
  const [busy, setBusy] = useState(false)

  // A guarded layer over the record (h 425-458): formFrom(load) is the
  // start, so an untouched form closes silently and a changed one asks.
  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged(form)
  const { requestClose, confirm } = useLayer(true, onCancel, { dirty: dirty && !busy, el: panelEl, focusFirst: options !== null, busy })
  const slow = useSlow(busy)

  useEffect(() => {
    let live = true
    apiJson<LoadOptions>(user, '/api/dispatches/options')
      .then((o) => live && setOptions(o))
      .catch((e) => live && setErrors([`Failed to load carriers and locations: ${errText(e)}`]))
    return () => {
      live = false
    }
  }, [user])

  const save = async () => {
    setBusy(true)
    try {
      onSaved(await apiJson<Result>(user, `/api/dispatches/${load.id}`, { method: 'PUT', body: form }))
    } catch (e) {
      setErrors(errList(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div ref={panelEl} className={os.inlinePanel} data-testid="edit-panel">
      <div className={styles.strong}>Edit load details</div>
      {options ? (
        <div className={inertClass(busy, slow)}>
          <DetailsFields form={form} set={(p) => setForm((f) => ({ ...f, ...p }))} options={options} current={load} />
        </div>
      ) : (
        !errors.length && <div className={styles.muted}>Loading…</div>
      )}
      {errors.length > 0 && (
        <div className={styles.errList} role="alert">
          {errors.map((e) => (
            <div key={e}>{e}</div>
          ))}
        </div>
      )}
      <div className={styles.actions}>
        <PendingButton className={pa.primaryBtn} busy={busy} pending="Saving…" disabled={!options || !formValid(form)} onClick={save}>
          Save details
        </PendingButton>
        <button type="button" className={pa.ghostBtn} disabled={busy} onClick={requestClose}>
          Cancel
        </button>
      </div>
      {confirm}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Orders & pallets, with loading verification
// ---------------------------------------------------------------------------

function OrdersAndPallets({
  rec,
  mayManage,
  busyOn,
  scan,
  setScan,
  scanMsg,
  onScan,
  onMove,
  onRemove,
  onClearUnexpected,
  onOpenPallet,
  onOpenOrder,
}: {
  rec: LoadRecord
  mayManage: boolean
  busyOn: string | null
  scan: string
  setScan: (v: string) => void
  scanMsg: { tone: string; text: string } | null
  onScan: () => void
  onMove: (path: string) => void
  onRemove: (palletId: string) => void
  onClearUnexpected: (palletId: string) => void
  onOpenPallet: (id: string) => void
  onOpenOrder: (id: string) => void
}) {
  const { load: l, moves: m } = rec
  const busy = busyOn !== null
  const pr = progress(rec)
  const tone = scanMsg ? SCAN_TONE[scanMsg.tone] || SCAN_TONE.bad : null
  const live = l.state !== 'dispatched' && l.state !== 'cancelled'
  return (
    <>
      {m.scan && (
        <div className={ls.loading} data-testid="loading-panel">
          <div className={ls.progressRow}>
            <span>Manual verification · {pr.text}</span>
            <div className={ls.bar}>
              <span style={{ width: pr.pct + '%' }} />
            </div>
          </div>
          {mayManage && (
          <div className={ls.scanRow}>
            <input
              className={ls.scanInput}
              aria-label="Pallet ID to verify"
              placeholder="Scan or type a pallet ID, then Enter"
              autoFocus
              value={scan}
              onChange={(e) => setScan(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onScan()
              }}
            />
            <PendingButton className={pa.primaryBtn} busy={busyOn === 'scan'} pending="Verifying…" disabled={busy || !scan.trim()} onClick={onScan}>
              Verify
            </PendingButton>
            {m.verify_all && (
              <PendingButton className={pa.ghostBtn} busy={busyOn === 'verify-all'} pending="Verifying…" disabled={busy} onClick={() => onMove('verify-all')}>
                Verify all by sight
              </PendingButton>
            )}
            {m.reset && (
              <PendingButton className={pa.ghostBtn} busy={busyOn === 'reset-loading'} pending="Clearing…" disabled={busy} onClick={() => onMove('reset-loading')}>
                Clear verification
              </PendingButton>
            )}
          </div>
          )}
          {scanMsg && tone && (
            <div className={ls.scanMsg} style={{ background: tone[0], color: tone[1] }} role="status" data-testid="scan-msg">
              {scanMsg.text}
            </div>
          )}
          {rec.unexpected.length > 0 && (
            <div className={ls.unexpected} data-testid="unexpected">
              <span className={styles.strong}>Verified but not on this load: resolve before departure</span>
              {rec.unexpected.map((u) => (
                <div key={u.id} className={ls.unexpectedRow}>
                  <span className={styles.strong}>{u.pallet_id}</span>
                  {u.other_load && <span>on {u.other_load}</span>}
                  {mayManage && <PendingButton className={styles.dangerBtn} busy={busyOn === `verifications/${u.id}/clear`} pending="Removing…" disabled={busy} onClick={() => onClearUnexpected(u.id)}>
                    Remove from verification
                  </PendingButton>}
                </div>
              ))}
            </div>
          )}
          <div className={ls.departRow}>
            {mayManage && m.depart.on && m.depart.blocked.length === 0 && (
              <PendingButton className={ls.departBtn} busy={busyOn === 'depart'} pending="Confirming…" disabled={busy} onClick={() => onMove('depart')}>
                Confirm departure
              </PendingButton>
            )}
            {m.depart.blocked.length > 0 && (
              <span className={ls.departBlocked} data-testid="depart-blocked">
                {m.depart.blocked.join(' ')}
              </span>
            )}
          </div>
        </div>
      )}

      <div className={pa.card} style={{ overflowX: 'auto', flex: 'none' }}>
        <div className={os.cardTitle}>Orders on this load: each keeps its own destination and paperwork</div>
        {rec.orders.length === 0 && <div className={pa.empty}>No orders yet. Orders come with the pallets you add.</div>}
        {rec.orders.map((o) => (
          <div key={o.id} className={`${ls.ordGrid} ${styles.line} ${styles.clickable}`} data-testid="load-order" {...openableRow(() => onOpenOrder(o.id))}>
            <span className={styles.orderRef}>{o.reference}</span>
            <span className={styles.ellipsis}>{o.customer_name || '—'}</span>
            <span className={`${styles.muted} ${styles.ellipsis}`}>→ {o.destination_name || '—'}
              {o.final_destination ? ' · final ' + o.final_destination : ''}
            </span>
            <span>
              {o.pallets} pallet{o.pallets === 1 ? '' : 's'}
            </span>
            <span>{o.boxes} boxes</span>
            <span>
              <ChipEl c={approvalChip(o.approval_status)} />
            </span>
          </div>
        ))}
      </div>

      <div className={pa.card} style={{ overflowX: 'auto', flex: 'none' }}>
        <div className={os.cardTitle}>Planned pallets</div>
        {rec.pallets.length === 0 && <div className={pa.empty}>No pallets on this load yet.</div>}
        {rec.pallets.map((p) => {
          const st: Chip = { label: PSTATE[p.state][0], bg: PSTATE[p.state][1], ink: PSTATE[p.state][2] }
          return (
            <div key={p.id} className={`${ls.palGrid} ${styles.line}`} data-testid="load-pallet">
              <button type="button" className={ls.link} onClick={() => onOpenPallet(p.id)}>
                {p.pallet_id}
              </button>
              <span>{p.order?.reference || '—'}</span>
              <span>{p.boxes} boxes</span>
              <span className={styles.stack}>
                <span className={`${styles.muted} ${styles.ellipsis}`}>{srcShort(p.source)}</span>
                {live &&
                  p.gates.map((g) => (
                    <span key={g} className={ls.gate}>
                      {g}
                    </span>
                  ))}
              </span>
              <span>
                <ChipEl c={st} />
              </span>
              <span>
                <ChipEl c={verificationChip(l, p)} />
              </span>
              <span>
                {mayManage && m.remove && (
                  <PendingButton className={pa.ghostSmall} title="Stays in stock; order quantity unchanged" busy={busyOn === `pallets/${p.id}/remove`} pending="Removing…" disabled={busy} onClick={() => onRemove(p.id)}>
                    Remove
                  </PendingButton>
                )}
              </span>
            </div>
          )
        })}
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Add pallets
// ---------------------------------------------------------------------------

function AddPalletsPanel({
  user,
  load,
  onClose,
  onAdded,
}: {
  user: any
  load: Load
  onClose: () => void
  onAdded: (r: Result) => void
}) {
  const [list, setList] = useState<GatedPallet[] | null>(null)
  const [sel, setSel] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const userRef = useRef(user)
  userRef.current = user
  const fetchList = useCallback(async () => {
    try {
      const r = await apiJson<{ pallets: GatedPallet[] }>(userRef.current, `/api/dispatches/${load.id}/candidates`)
      setList(r.pallets)
    } catch (e) {
      setList([])
      setError(`Failed to load pallets: ${errText(e)}`)
    }
  }, [load.id])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  // A guarded layer over the record (h 425-458): a selection nobody has
  // added yet is the unsaved change.
  const panelEl = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: sel.length > 0 && !busy, el: panelEl, modal: true, busy })
  const slow = useSlow(busy)

  const add = async () => {
    setBusy(true)
    try {
      onAdded(await apiJson<Result>(userRef.current, `/api/dispatches/${load.id}/pallets`, { method: 'POST', body: { pallet_ids: sel } }))
    } catch (e) {
      setError(errText(e))
      setSel([])
      fetchList()
    } finally {
      setBusy(false)
    }
  }

  return (
    <SdPanel
      title={`Add pallets to ${load.reference}`}
      onCancel={requestClose}
      confirm={confirm}
      panelEl={panelEl}
      busy={busy}
      bodyClass={inertClass(busy, slow)}
      foot={
        <PendingButton className={o.primary} busy={busy} pending="Adding…" disabled={sel.length === 0} onClick={add}>
          {sel.length ? `Add ${sel.length} pallet${sel.length === 1 ? '' : 's'}` : 'Add pallets'}
        </PendingButton>
      }
    >
          <p className={sd.text}>
            Only confirmed pallets on approved orders with matching lids and no hold can be released to a load. Others are listed with the reason.
          </p>
          {error && (
            <div className={styles.alert} role="alert">
              {error}
            </div>
          )}
          {list && list.length === 0 && !error && <div className={pa.empty}>No other pallets carry an order.</div>}
          {(list || []).map((p) => {
            const gated = p.gates.length > 0
            const st: Chip = { label: PSTATE[p.state][0], bg: PSTATE[p.state][1], ink: PSTATE[p.state][2] }
            return (
              <label key={p.id} className={ls.candRow} data-testid="candidate" style={gated ? { opacity: 0.75 } : undefined}>
                <input
                  type="checkbox"
                  aria-label={p.pallet_id}
                  disabled={gated}
                  checked={sel.includes(p.id)}
                  onChange={(e) => setSel((s) => (e.target.checked ? [...s, p.id] : s.filter((x) => x !== p.id)))}
                />
                <span className={ls.candBody}>
                  <span className={styles.strong}>{p.pallet_id}</span>
                  <span className={styles.sub}>{candidateText(p)}</span>
                  {p.gates.map((g) => (
                    <span key={g} className={ls.gate}>
                      {g}
                    </span>
                  ))}
                </span>
                <ChipEl c={st} />
              </label>
            )
          })}
    </SdPanel>
  )
}

// ---------------------------------------------------------------------------
// Documents and history
// ---------------------------------------------------------------------------

function DocumentsTab({ rec }: { rec: LoadRecord }) {
  if (rec.documents.length === 0) return <div className={pa.empty}>Documents appear once orders are on the load.</div>
  return (
    <>
      {rec.documents.map((d) => (
        <div key={d.order_id} className={ls.docCard} data-testid="load-docs">
          <div className={styles.strong}>
            {d.order_reference} <span className={styles.muted}>· {d.customer_name || '—'}</span>
          </div>
          <div className={ls.docItems}>
            {d.kinds.map((k) => (
              <div key={k} className={ls.docItem}>
                {k}
                <span>{d.note}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className={styles.note} style={{ flex: 'none' }}>
        Printing these documents is not deployed yet. This tab lists the paperwork each order on the load will need.
      </div>
    </>
  )
}

function HistoryTab({ rec }: { rec: LoadRecord }) {
  if (rec.history.length === 0) return <div className={pa.empty}>No history yet.</div>
  return (
    <div className={styles.facts}>
      {rec.history.map((h) => (
        <div key={h.id} className={styles.hist} data-testid="load-history">
          <span className={styles.histAt}>{fmtStamp(h.at)}</span>
          <span className={styles.stack}>
            <span>{h.what}</span>
            <span className={styles.sub}>{h.by + ' · ' + h.how}</span>
            {h.reason && <span className={styles.sub}>{h.reason}</span>}
          </span>
        </div>
      ))}
    </div>
  )
}

// V4 DD_HELP. Printing is not built, so the Documents tab says so instead
// of describing reprints.
const HELP: Record<LoadTab, { title: string; items: [string, React.ReactNode][] }> = {
  load: {
    title: 'What this tab holds',
    items: [
      [
        'The truck, not the cargo',
        'Carrier, vehicle, booking, dock, driver and instructions. Everything about the vehicle and the departure. What is on it lives on the next tab.',
      ],
      [
        'Planned and actual departure',
        <div className={styles.helpLines}>
          <div>
            <strong>Planned</strong> when the load is booked to leave
          </div>
          <div>
            <strong>Actual</strong> stamped when departure is confirmed, with the person and whether it came from Control or the crew app
          </div>
          <div>Until departure is confirmed nothing has left stock, whatever the planned time says.</div>
        </div>,
      ],
      [
        'Freight references live on the order',
        'Airway bill, flight, container, forwarder and receiving establishment are order data, not load data, so a shared truck never carries one customer’s paperwork onto another’s.',
      ],
    ],
  },
  op: {
    title: 'Orders and pallets on this load',
    items: [
      [
        'Pallets bring their orders with them',
        'You add pallets, not orders; the orders listed are whichever ones those pallets serve. One truck can carry several orders and one order can span two trucks.',
      ],
      [
        'Every pallet must pass the gates',
        'Confirmed, no discrepancy, no hold on the pallet or its source, order approved, applied lid matching the required lid, not already on another active load. A pallet that fails is named with its reason rather than silently excluded.',
      ],
      [
        'Verification is a set, not a count',
        'Departure needs the verified pallets to be exactly the planned pallets. The right number with a wrong identity is blocked and the identity named. Removing a pallet clears its verification and returns it to stock with the order quantity unchanged.',
      ],
    ],
  },
  docs: {
    title: 'Documents for this load',
    items: [
      [
        'One set per order on the load',
        'Delivery docket, packing list and transfer record are produced per order, because each goes to a different customer.',
      ],
      [
        'Draft until departure',
        'Anything printed before the load leaves is marked Draft, and the final version reflects what actually departed. Pallet identity labels are the exception. They are never marked Draft, since the pallet exists either way.',
      ],
      ['Printing is not deployed yet', 'Nothing prints from this tab yet, so there are no reprints or print history to show.'],
    ],
  },
  hist: {
    title: 'What the history records',
    items: [
      [
        'Every event, with a person and a source',
        'Creation, pallets added and removed, loading started, each scan, verification by sight, departure and cancellation. Each with the user, the timestamp and whether it was Control or the crew app.',
      ],
      ['Nothing is edited away', 'Blocked scans and cleared verifications stay in the record. The history is how a disputed departure is settled later.'],
    ],
  },
}

function TabHelp({ tab, onClose }: { tab: LoadTab; onClose: () => void }) {
  const h = HELP[tab]
  return (
    <div className={styles.helpBox} data-testid="load-help">
      <button type="button" className={styles.helpX} title="Hide help" aria-label="Hide help" onClick={onClose}>
        ×
      </button>
      <div className={styles.helpTitle}>{h.title}</div>
      {h.items.map(([k, v]) => (
        <div key={k} className={styles.helpItem}>
          <span>{k}</span>
          {typeof v === 'string' ? <p>{v}</p> : v}
        </div>
      ))}
    </div>
  )
}
